import { useContext, useMemo } from "react";
import { FlightConfigProvider } from "@b2bportal/core-flights";
import { useModuleBEM, useTenantImages } from "@b2bportal/core-themes";
import { Funnel } from "@b2bportal/core-types";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { HomePageActivitySearch } from "@tenant/activities";
import { HomePageCarSearch } from "@tenant/cars";
import { HomePageFlightSearch } from "@tenant/flights";
import { HomePageHotelSearch } from "@tenant/hotels";
import { HomePagePackageSearch } from "@tenant/packages";
import { flightsAppConfig } from "../Flights/config";
import { BannerSection, useHomepageBanners } from "./BannerSection";
import styles from "./Homepage.module.scss";
import { HotelDealsSection } from "./HotelDealsSection";
import { WalletOffersSection } from "./WalletOffersSection";
import { UserContext } from "@/App";
import { useClearOfferFunnelEntrySourceOnLodgingReturn } from "@/tracking/offers/useClearOfferFunnelEntrySourceOnLodgingReturn";

export const Homepage = ({ funnel }: { funnel: Funnel }) => {
  const { t } = useI18nContext();
  const { sessionInfo } = useContext(UserContext);
  const { hasBanner } = useHomepageBanners();
  const [block, cn] = useModuleBEM(styles, "Homepage", {
    "has-banner": hasBanner,
  });
  const images = useTenantImages();

  useClearOfferFunnelEntrySourceOnLodgingReturn(funnel);

  const homepageImageSrc = useMemo(() => {
    switch (funnel) {
      case Funnel.Ground:
        return images.carsHomepageImage;
      case Funnel.Activities:
        return images.activitiesHomepageImage;
      case Funnel.Lodging:
      case Funnel.Air:
      case Funnel.Package:
        return images.homepageImage;
      case Funnel.Trips:
        return;
      default:
        ensureExhaustive(funnel);
    }
  }, [funnel, images]);

  const { title, subtitle } = useMemo(() => {
    switch (funnel) {
      case Funnel.Activities:
        return {
          title: t("home.activities.title"),
          subtitle: t("home.activities.subtitle"),
        };
      case Funnel.Ground:
        return {
          title: t("core-cars.homepage.title"),
          subtitle: t("core-cars.homepage.subtitle"),
        };
      case Funnel.Air:
        return {
          title: t("core-flights.homepage.title"),
          subtitle: t("core-flights.homepage.subtitle"),
        };
      case Funnel.Lodging:
        return {
          title: t("core-hotels.homepage.title"),
          subtitle: t("core-hotels.homepage.subtitle"),
        };
      case Funnel.Package:
        return {
          title: t("core-packages.homepage.title"),
          subtitle: t("core-packages.homepage.subtitle"),
        };

      case Funnel.Trips:
        return;
      default:
        ensureExhaustive(funnel);
    }
  }, [funnel, sessionInfo?.userInfo?.firstName, t]);

  const searchComponent = useMemo(() => {
    switch (funnel) {
      case Funnel.Air:
        return (
          <FlightConfigProvider context={flightsAppConfig}>
            <HomePageFlightSearch />
          </FlightConfigProvider>
        );
      case Funnel.Lodging:
        return <HomePageHotelSearch />;
      case Funnel.Ground:
        return <HomePageCarSearch />;
      case Funnel.Package:
        return <HomePagePackageSearch />;
      case Funnel.Activities:
        return <HomePageActivitySearch />;
      case Funnel.Trips:
        return;
      default:
        ensureExhaustive(funnel);
    }
  }, [funnel]);

  return (
    <>
      <div className={block}>
        <div className={cn("container")}>
          <BannerSection />
          <div className={cn("content")}>
            <div className={cn("search-content")}>
              <div className={cn("merchandising-content")}>
                <h1 className={cn("title")}>{title}</h1>
                <h2 className={cn("subtitle")}>{subtitle}</h2>
              </div>
              {searchComponent}
            </div>
            <img
              src={homepageImageSrc}
              alt="luggage-illustration"
              className={cn("image")}
            />
          </div>
        </div>
        <WalletOffersSection funnel={funnel} />
        <HotelDealsSection funnel={funnel} />
      </div>
    </>
  );
};

export default Homepage;
